import React, { useMemo, useState } from "react"
import Chart from "react-apexcharts"

const CHART_COLORS = [
  "#A78BFA",
  "#34D399",
  "#FB923C",
  "#F87171",
  "#FCD34D",
  "#2DD4BF",
  "#F472B6",
  "#60A5FA",
]

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "chronological", icon: "⇅", label: "Sort", color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending", icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending", icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: current.bg,
        border: `1.5px solid ${current.border}`,
        borderRadius: "10px",
        padding: "5px 12px",
        cursor: "pointer",
        outline: "none",
        transition: "all 0.18s ease",
        whiteSpace: "nowrap",
        userSelect: "none",
        minWidth: "115px",
      }}
    >
      <span
        style={{
          fontSize: "15px",
          lineHeight: 1,
          color: current.color,
          fontWeight: 700,
          minWidth: "16px",
          textAlign: "center",
        }}
      >
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span
          style={{
            fontSize: "8px",
            fontWeight: 700,
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            lineHeight: 1.1,
          }}
        >
          Sort Order
        </span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: current.color, lineHeight: 1.3 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

const BarChart = ({
  title,
  categories = [],
  series = [],
  horizontal = false,
  pound,
  colorIndex = 0,
  onBarClick,
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const handleToggle = () => {
    const next =
      sortOrder === "chronological"
        ? "ascending"
        : sortOrder === "ascending"
        ? "descending"
        : "chronological"
    setSortOrder(next)
  }

  const color = CHART_COLORS[colorIndex % CHART_COLORS.length]
  const rawData = series?.[0]?.data || []

  const sortedIndices = useMemo(() => {
    const indices = Array.from({ length: categories.length }, (_, i) => i)
    if (sortOrder === "ascending") {
      indices.sort((a, b) => (Number(rawData[a]) || 0) - (Number(rawData[b]) || 0))
    } else if (sortOrder === "descending") {
      indices.sort((a, b) => (Number(rawData[b]) || 0) - (Number(rawData[a]) || 0))
    }
    return indices
  }, [categories, rawData, sortOrder])

  const displayCategories = sortedIndices.map(i => categories[i])
  const displayData = sortedIndices.map(i => rawData[i] ?? 0)

  const displaySeries = [
    {
      ...(series?.[0] || {}),
      data: displayData,
    },
  ]

  const getFormatter = val => {
    if (!val && val !== 0) return "N/A"
    const rounded = Math.round(Number(val))
    return pound
      ? `£${rounded.toLocaleString("en-GB")}`
      : rounded.toLocaleString("en-GB")
  }

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      background: "transparent",
      events: onBarClick
        ? {
            dataPointSelection: (_e, _ctx, config) => {
              const idx = config.dataPointIndex
              if (idx < 0) return
              onBarClick(displayCategories[idx])
            },
          }
        : {},
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 100 },
      },
      dropShadow: {
        enabled: true,
        color,
        top: 4,
        blur: 10,
        opacity: 0.12,
      },
    },

    plotOptions: {
      bar: {
        horizontal,
        barHeight: "55%",
        columnWidth: "45%",
        borderRadius: 5,
        borderRadiusApplication: "end",
        distributed: true,
        dataLabels: { position: horizontal ? "center" : "top" },
      },
    },

    stroke: {
      width: 0,
    },

    dataLabels: {
      enabled: horizontal,
      formatter: val => getFormatter(val),
      style: {
        fontSize: "11px",
        fontWeight: 700,
        colors: ["#fff"],
      },
    },

    fill: {
      type: "solid",
      opacity: 1,
    },

    colors: CHART_COLORS,

    xaxis: {
      categories: displayCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -90,
        style: {
          colors: "#94a3b8",
          fontSize: "12px",
          fontWeight: 500,
        },
        formatter: val => (typeof val === "number" ? getFormatter(val) : val),
      },
    },

    yaxis: {
      labels: {
        maxWidth: 160,
        style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
        formatter: val => (typeof val === "number" ? getFormatter(val) : val),
      },
    },

    legend: { show: false },

    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: horizontal } },
      yaxis: { lines: { show: !horizontal } },
      padding: { top: 0, right: 16, bottom: 0, left: horizontal ? 20 : 8 },
    },

    tooltip: {
      theme: "dark",
      style: { fontSize: "12px" },
      x: { formatter: val => val },
      y: { formatter: val => getFormatter(val) },
    },

    states: {
      hover: { filter: { type: "lighten", value: 0.08 } },
      active: { filter: { type: "darken", value: 0.1 } },
    },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        border: `1px solid ${color}22`,
        boxShadow: `0 4px 24px ${color}14, 0 1px 4px rgba(0,0,0,0.04)`,
        position: "relative",
        overflow: "hidden",
        marginBottom: "1.5rem",
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${color}, ${color}88)`,
          borderRadius: "16px 16px 0 0",
        }}
      />

      <div style={{ padding: "20px 16px 8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            marginBottom: "4px",
            padding: "0 8px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: color,
                boxShadow: `0 0 8px ${color}88`,
              }}
            />
            <h4
              style={{
                margin: 0,
                fontSize: "15px",
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              {title}
            </h4>
          </div>

          <SortButton sortOrder={sortOrder} onToggle={handleToggle} />
        </div>

        <Chart options={options} series={displaySeries} type="bar" height={320} />
      </div>
    </div>
  )
}

export default BarChart
